import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom'; // Import Navigate for redirects
import { ThemeProvider, createTheme, CssBaseline, Box, Toolbar, useMediaQuery, useTheme } from '@mui/material';
import Sidebar from './components/Sidebar';
import { TaskManagement, BudgetTracking, GuestListManagement, CalendarIntegration, Reports, MultiViewBudgetTracking, MultiViewTaskManagement, MultiViewGuestListManagement, MultiViewCalendarIntegration, MultiViewReports } from './features';
import TopBar from './components/TopBar';
import { SwitchTransition, CSSTransition } from 'react-transition-group';
import './App.css';
import { Login, Register, Home, FriendsPage, Support, Privacy, Settings } from './pages';
import ResetPassword from './pages/resetPassword';
import Cookies from 'js-cookie';
import { AlertProvider } from './contexts/AlertContext';
import EventSetup from './pages/eventSetup';
import Invitation from './pages/invitation';
import { sidebarWidth, toolbarSx } from './utils/appLayout';

function App() {
  const [darkMode, setDarkMode] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const nodeRef = useRef(null);
  const navigate = useNavigate();
  const isMobile = useMediaQuery(useTheme().breakpoints.down('md'));
  const isLoggedIn = Cookies.get('token'); // Check if user is logged in

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    setDarkMode(mediaQuery.matches);

    const handleThemeChange = (e) => {
      setDarkMode(e.matches);
    };

    mediaQuery.addEventListener('change', handleThemeChange);

    return () => {
      mediaQuery.removeEventListener('change', handleThemeChange);
    };
  }, [isMobile]);

  useEffect(() => {
    setOpen(false);
  }, [isMobile, location.pathname, location.search]);

  const theme = createTheme({
    palette: {
      mode: darkMode ? 'dark' : 'light',
    },
  });

  const toggleDrawer = () => {
    setOpen((prev) => !prev);
  };

  const handleReportClick = (report, eventId) => {
    toggleDrawer();
    navigate(`/events/${eventId}/reports/${report.toLowerCase()}`);
  };

  // Define routes where the sidebar should be hidden
  const sidebarHiddenRoutes = ['/', '/login', '/register', '/reset-password', '/home', '/friends', '/support', '/privacy', '/settings'];
  const isInvitation = location.pathname === '/invite';
  const isSidebarVisible = !isInvitation && !sidebarHiddenRoutes.includes(location.pathname);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AlertProvider>
        {!isInvitation && <TopBar toggleDrawer={toggleDrawer} isSidebarVisible={isSidebarVisible} darkMode={darkMode} setDarkMode={setDarkMode} />}

        <Box sx={{ display: 'flex' }}>
          {isSidebarVisible && (
            <Sidebar
              open={open}
              toggleDrawer={toggleDrawer}
              handleReportClick={handleReportClick}
            />
          )}

          <Box component="main" sx={{ flexGrow: 1, minWidth: 0, p: { xs: 1.5, sm: 2, md: 3 }, width: isSidebarVisible && !isMobile ? `calc(100% - ${sidebarWidth}px)` : '100%' }}>
            {!isInvitation && <Toolbar sx={toolbarSx} />}
            <SwitchTransition>
              <CSSTransition key={location.key} nodeRef={nodeRef} classNames="fade" timeout={300}>
                <div ref={nodeRef}>
                  <Routes location={location}>
                    {/* Conditional routing for '/' */}
                    <Route path="/" element={isLoggedIn ? <Navigate to="/home" /> : <Navigate to="/login" />} />

                    {/* Public routes */}
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/support" element={<Support />} />
                    <Route path="/privacy" element={<Privacy />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/invite" element={<Invitation />} />
                    
                    {/* Protected routes */}
                    <Route path="/home" element={isLoggedIn ? <Home /> : <Navigate to="/login" />} />
                    <Route path="/friends" element={isLoggedIn ? <FriendsPage /> : <Navigate to="/login" />} />
                    <Route path="/settings" element={isLoggedIn ? <Settings /> : <Navigate to="/login" />} />

                    {/* Event-specific protected routes */}
                    <Route path="/events/:eventId/setup" element={isLoggedIn ? <EventSetup /> : <Navigate to="/login" />} />
                    <Route path="/events/:eventId/tasks" element={isLoggedIn ? <TaskManagement /> : <Navigate to="/login" />} />
                    <Route path="/events/:eventId/budget" element={isLoggedIn ? <BudgetTracking /> : <Navigate to="/login" />} />
                    <Route path="/events/:eventId/guests" element={isLoggedIn ? <GuestListManagement /> : <Navigate to="/login" />} />
                    <Route path="/events/:eventId/calendar" element={isLoggedIn ? <CalendarIntegration /> : <Navigate to="/login" />} />
                    <Route path="/events/:eventId/reports/:reportType" element={isLoggedIn ? <Reports /> : <Navigate to="/login" />} />
                    <Route path="/events/multi-view/budget" element={isLoggedIn ? <MultiViewBudgetTracking /> : <Navigate to="/login" />} />
                    <Route path="/events/multi-view/tasks" element={isLoggedIn ? <MultiViewTaskManagement /> : <Navigate to="/login" />} />
                    <Route path="/events/multi-view/guests" element={isLoggedIn ? <MultiViewGuestListManagement /> : <Navigate to="/login" />} />
                    <Route path="/events/multi-view/calendar" element={isLoggedIn ? <MultiViewCalendarIntegration /> : <Navigate to="/login" />} />
                    <Route path="/events/multi-view/reports/:reportType" element={isLoggedIn ? <MultiViewReports /> : <Navigate to="/login" />} />
                  </Routes>
                </div>
              </CSSTransition>
            </SwitchTransition>
          </Box>
        </Box>
      </AlertProvider>
    </ThemeProvider>
  );
}

export default App;
